import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, RadialBarChart, RadialBar, PolarAngleAxis, Legend
} from "recharts";
import { TrendingUp, DollarSign, Briefcase, Clock, Users, ArrowUp, ArrowDown, ArrowUpDown, Trophy, FileDown, BarChart3 } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { toast } from "sonner";

const KPI_LABELS = {
  presentasi: "Presentasi",
  principal_relationship: "Principal Relationship",
  technical_proposal: "Technical Proposal",
  sertifikasi: "Sertifikasi",
  self_skill: "Self Skill Development",
};
const KPI_KEYS = Object.keys(KPI_LABELS);
const KPI_COLORS = ["#007AFF", "#0A0A0A", "#334155", "#64748B", "#94A3B8"];

const money = (v) => "Rp " + (Number(v || 0)).toLocaleString("id-ID");

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="card-tech p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-mini">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="mt-3 font-heading text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function KpiBar({ totals }) {
  const data = Object.entries(totals || {}).map(([k, v]) => ({ name: KPI_LABELS[k] || k, value: v }));
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 16, left: -8, bottom: 30 }}>
          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} angle={-15} textAnchor="end" height={50} interval={0} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {data.map((_, i) => <Cell key={i} fill={KPI_COLORS[i % 5]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TeamKpiStacked({ members }) {
  // Grouped bar per KPI dimension, one series per member
  const data = KPI_KEYS.map((k) => {
    const row = { dim: KPI_LABELS[k] };
    members.forEach((m) => { row[m.name] = m.kpi_totals?.[k] ?? 0; });
    return row;
  });
  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 16, left: -8, bottom: 30 }}>
          <XAxis dataKey="dim" stroke="hsl(var(--muted-foreground))" fontSize={11} angle={-15} textAnchor="end" height={50} interval={0} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {members.map((m, i) => (
            <Bar key={m.user_id} dataKey={m.name} fill={KPI_COLORS[i % KPI_COLORS.length]} radius={[3, 3, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ScoreRadial({ score }) {
  const data = [{ name: "score", value: score, fill: "#007AFF" }];
  return (
    <div className="h-64 relative">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar background dataKey="value" cornerRadius={30} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="font-heading text-5xl font-black tracking-tight">{score}</div>
        <div className="label-mini">KPI Score</div>
      </div>
    </div>
  );
}

const RANK_MEDAL = ["bg-amber-400 text-amber-950", "bg-slate-300 text-slate-900", "bg-orange-400 text-orange-950"];

function Ranking({ members }) {
  const ranked = [...members].sort((a, b) => (b.kpi_score || 0) - (a.kpi_score || 0));
  const max = Math.max(1, ...ranked.map((m) => m.kpi_score || 0));
  return (
    <div className="space-y-3" data-testid="team-ranking">
      {ranked.map((m, i) => (
        <div key={m.user_id} className="flex items-center gap-4" data-testid={`rank-row-${i + 1}`}>
          <div className={`h-8 w-8 rounded-sm flex items-center justify-center font-black text-sm shrink-0 ${
            i < 3 ? RANK_MEDAL[i] : "bg-accent text-foreground"
          }`}>
            {i + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold text-sm truncate">{m.name}</div>
              <div className="mono text-sm font-bold">{m.kpi_score}</div>
            </div>
            <div className="h-2 mt-1.5 bg-accent rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${Math.max(4, (m.kpi_score / max) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      ))}
      {ranked.length === 0 && (
        <div className="text-sm text-muted-foreground">Belum ada anggota member.</div>
      )}
    </div>
  );
}

const SORT_COLS = [
  { key: "name", label: "Nama", align: "left", getter: (m) => (m.name || "").toLowerCase() },
  { key: "kpi_score", label: "KPI Score", align: "right", getter: (m) => Number(m.kpi_score || 0) },
  { key: "pipeline_value", label: "Pipeline", align: "right", getter: (m) => Number(m.pipeline_value || 0) },
  { key: "won_value", label: "Won", align: "right", getter: (m) => Number(m.won_value || 0) },
  { key: "total_hours", label: "Jam", align: "right", getter: (m) => Number(m.total_hours || 0) },
  { key: "projects_count", label: "Project", align: "right", getter: (m) => Number(m.projects_count || 0) },
];

function SortButton({ label, active, dir, onClick, align, testid }) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? "text-foreground" : ""} ${align === "right" ? "justify-end w-full" : ""}`}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
}

export default function Dashboard() {
  const { user, isLeaderView } = useAuth();
  const isLeader = isLeaderView;
  const [data, setData] = useState(null);
  const [team, setTeam] = useState(null);
  const [sortKey, setSortKey] = useState("kpi_score");
  const [sortDir, setSortDir] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [quarter, setQuarter] = useState("all");
  const [qCompare, setQCompare] = useState(null);
  const [qLoading, setQLoading] = useState(false);

  const applyQuarter = (q) => {
    setQuarter(q);
    if (q === "all") { setDateFrom(""); setDateTo(""); return; }
    const y = new Date().getFullYear();
    const ranges = {
      Q1: [`${y}-01-01`, `${y}-03-31`],
      Q2: [`${y}-04-01`, `${y}-06-30`],
      Q3: [`${y}-07-01`, `${y}-09-30`],
      Q4: [`${y}-10-01`, `${y}-12-31`],
    };
    const [f, t] = ranges[q] || ["", ""];
    setDateFrom(f); setDateTo(t);
  };

  useEffect(() => {
    (async () => {
      const me = await api.get("/forecast/me");
      setData(me.data);
      if (isLeader) {
        const params = new URLSearchParams();
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        const t = await api.get(`/forecast/team?${params.toString()}`);
        setTeam(t.data);
      }
    })().catch((e) => console.error(e));
  }, [isLeader, dateFrom, dateTo]);

  // Load Q1–Q4 comparison (current year) — once per year
  useEffect(() => {
    if (!isLeader) return;
    (async () => {
      setQLoading(true);
      try {
        const y = new Date().getFullYear();
        const ranges = {
          Q1: [`${y}-01-01`, `${y}-03-31`],
          Q2: [`${y}-04-01`, `${y}-06-30`],
          Q3: [`${y}-07-01`, `${y}-09-30`],
          Q4: [`${y}-10-01`, `${y}-12-31`],
        };
        const results = await Promise.all(
          Object.entries(ranges).map(async ([label, [f, t]]) => {
            const { data } = await api.get(`/forecast/team?date_from=${f}&date_to=${t}`);
            return { quarter: label, pipeline: data.total_pipeline, won: data.total_won };
          })
        );
        setQCompare(results);
      } catch (e) { console.error(e); }
      finally { setQLoading(false); }
    })();
  }, [isLeader]);

  const memberOnly = useMemo(
    () => (team?.members || []).filter((m) => m.role !== "team_leader"),
    [team]
  );

  const sortedMembers = useMemo(() => {
    const col = SORT_COLS.find((c) => c.key === sortKey);
    if (!col) return memberOnly;
    const list = [...memberOnly].sort((a, b) => {
      const av = col.getter(a);
      const bv = col.getter(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [memberOnly, sortKey, sortDir]);

  const [pageMembers, memberCtrl] = usePagination(sortedMembers, 10);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("pc_token");
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await fetch(`${API}/reports/executive-pdf?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = dateFrom || dateTo ? `-${dateFrom || "start"}_${dateTo || "end"}` : "";
      a.download = `executive-report${suffix}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF berhasil diunduh");
    } catch (err) {
      toast.error("Gagal generate PDF");
    }
  };

  // Aggregate stats across members-only for the leader "all members" overview
  const teamAgg = useMemo(() => {
    const totals = KPI_KEYS.reduce((acc, k) => { acc[k] = 0; return acc; }, {});
    memberOnly.forEach((m) => {
      KPI_KEYS.forEach((k) => { totals[k] += Number(m.kpi_totals?.[k] || 0); });
    });
    const avgScore = memberOnly.length
      ? Math.round((memberOnly.reduce((s, m) => s + Number(m.kpi_score || 0), 0) / memberOnly.length) * 100) / 100
      : 0;
    return { totals, avgScore };
  }, [memberOnly]);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-mini">Dashboard</div>
          <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tight">
            {isLeader ? "Forecast Team" : "Forecast Saya"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {isLeader
              ? "Ringkasan performa seluruh anggota tim presales."
              : `Halo ${user?.name}, berikut ringkasan performa Anda.`}
          </p>
        </div>
        {isLeader && (
          <Button variant="outline" onClick={downloadPdf} className="rounded-sm" data-testid="export-pdf-button">
            <FileDown className="h-4 w-4 mr-2" /> Export Executive PDF
          </Button>
        )}
      </div>

      {isLeader && (
        <div className="card-tech p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="team-filters">
          <div>
            <div className="label-mini mb-1">Quarter</div>
            <div className="flex gap-1 flex-wrap">
              {["all", "Q1", "Q2", "Q3", "Q4"].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => applyQuarter(q)}
                  data-testid={`quarter-${q}`}
                  className={`text-xs px-3 py-1 rounded-sm border transition-colors ${
                    quarter === q ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"
                  }`}
                >{q === "all" ? "Semua" : q}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-mini mb-1">Dari Tanggal</div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setQuarter("custom"); }}
              className="w-full h-9 px-3 rounded-sm border border-input bg-background text-sm"
              data-testid="dashboard-date-from"
            />
          </div>
          <div>
            <div className="label-mini mb-1">Sampai Tanggal</div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setQuarter("custom"); }}
              className="w-full h-9 px-3 rounded-sm border border-input bg-background text-sm"
              data-testid="dashboard-date-to"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => applyQuarter("all")}
              className="h-9 w-full px-3 rounded-sm border border-border text-xs hover:bg-accent"
              data-testid="dashboard-clear-filter"
            >Reset Filter</button>
          </div>
        </div>
      )}

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {isLeader && team ? (
          <>
            <StatCard label="Anggota Member" value={memberOnly.length} icon={Users} testid="stat-members" />
            <StatCard label="Total Pipeline" value={money(team.total_pipeline)} icon={Briefcase} testid="stat-pipeline" />
            <StatCard label="Total Won" value={money(team.total_won)} icon={DollarSign} testid="stat-won" />
            <StatCard label="Rata-rata KPI" value={teamAgg.avgScore} icon={TrendingUp} testid="stat-avg-kpi" />
          </>
        ) : (
          <>
            <StatCard label="KPI Score" value={data.kpi_score} icon={TrendingUp} testid="stat-score" />
            <StatCard label="Pipeline" value={money(data.pipeline_value)} icon={Briefcase} testid="stat-pipeline" />
            <StatCard label="Won" value={money(data.won_value)} icon={DollarSign} testid="stat-won" />
            <StatCard label="Total Jam" value={`${data.total_hours}h`} icon={Clock} testid="stat-hours" />
          </>
        )}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isLeader && team ? (
          <>
            <div className="card-tech p-6 lg:col-span-2" data-testid="team-kpi-stats">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-mini">Statistik Tim</div>
                  <h3 className="font-heading text-xl font-bold mt-1">Perolehan KPI per Member</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Perbandingan skor pada setiap dimensi KPI antar anggota member.
                  </p>
                </div>
              </div>
              {memberOnly.length > 0 ? (
                <TeamKpiStacked members={memberOnly} />
              ) : (
                <div className="h-80 flex items-center justify-center text-sm text-muted-foreground">
                  Belum ada data member.
                </div>
              )}
            </div>
            <div className="card-tech p-6" data-testid="team-ranking-card">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-500" />
                <div className="label-mini">Leaderboard</div>
              </div>
              <h3 className="font-heading text-xl font-bold mt-1 mb-4">Ranking KPI</h3>
              <Ranking members={memberOnly} />
            </div>
          </>
        ) : (
          <>
            <div className="card-tech p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-mini">KPI Breakdown</div>
                  <h3 className="font-heading text-xl font-bold mt-1">Performance per Dimensi</h3>
                </div>
              </div>
              <KpiBar totals={data.kpi_totals} />
            </div>
            <div className="card-tech p-6">
              <div className="label-mini">Weighted</div>
              <h3 className="font-heading text-xl font-bold mt-1 mb-2">Overall Score</h3>
              <ScoreRadial score={data.kpi_score} />
            </div>
          </>
        )}
      </div>

      {/* Team table (leader only, members-only + sortable) */}
      {isLeader && qCompare && (
        <div className="card-tech p-6" data-testid="quarter-compare-card">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="h-4 w-4" />
            <div className="label-mini">Trend {new Date().getFullYear()}</div>
          </div>
          <h3 className="font-heading text-xl font-bold mb-4">Perbandingan Pipeline per Quarter</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={qCompare} margin={{ top: 10, right: 16, left: 0, bottom: 10 }}>
                <XAxis dataKey="quarter" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickFormatter={(v) => v >= 1e9 ? `${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `${(v/1e6).toFixed(0)}M` : v}
                />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                  formatter={(v) => "Rp " + Number(v).toLocaleString("id-ID")}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="pipeline" name="Pipeline" fill="#007AFF" radius={[4, 4, 0, 0]} />
                <Bar dataKey="won" name="Won" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {qLoading && <div className="text-xs text-muted-foreground mt-2">Memuat data quarter…</div>}
        </div>
      )}

      {isLeader && team && (
        <div className="card-tech p-0 overflow-hidden">
          <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label-mini">All Members</div>
              <h3 className="font-heading text-xl font-bold mt-1">Ringkasan Anggota Tim</h3>
            </div>
            <div className="text-xs text-muted-foreground">
              Klik header untuk mengurutkan ({sortDir === "asc" ? "asc" : "desc"} · {SORT_COLS.find((c) => c.key === sortKey)?.label})
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-forecast-table">
              <thead className="bg-accent text-xs uppercase tracking-widest">
                <tr>
                  {SORT_COLS.map((c) => (
                    <th key={c.key} className={`px-6 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}>
                      <SortButton
                        label={c.label}
                        active={sortKey === c.key}
                        dir={sortDir}
                        onClick={() => toggleSort(c.key)}
                        align={c.align}
                        testid={`sort-${c.key}`}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedMembers.length === 0 && (
                  <tr><td colSpan={SORT_COLS.length} className="px-6 py-10 text-center text-muted-foreground">Belum ada anggota member.</td></tr>
                )}
                {pageMembers.map((m) => (
                  <tr key={m.user_id} className="border-t border-border hover:bg-accent/50 transition-colors" data-testid={`member-row-${m.user_id}`}>
                    <td className="px-6 py-3 font-medium">{m.name}</td>
                    <td className="px-6 py-3 text-right mono">{m.kpi_score}</td>
                    <td className="px-6 py-3 text-right mono">{money(m.pipeline_value)}</td>
                    <td className="px-6 py-3 text-right mono">{money(m.won_value)}</td>
                    <td className="px-6 py-3 text-right mono">{m.total_hours}h</td>
                    <td className="px-6 py-3 text-right mono">{m.projects_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination ctrl={memberCtrl} testidPrefix="dashboard-pg" />
        </div>
      )}
    </div>
  );
}
